/**
 * What each subscription tier grants.
 *
 * The decision that shapes this file: **no mathematics is ever behind the
 * paywall.** Every published lesson, the whole Math Map, mastery tracking and
 * the placement test are free forever. What Premium buys is *volume* and
 * *analysis* — unlimited practice, detailed progress, advanced courses as they
 * land.
 *
 * That is a product choice and a deliberate one. A child who cannot pay should
 * not be taught less mathematics; charging for the lesson itself would make the
 * platform's stated purpose a marketing line. Limiting how many problems can be
 * generated in a day costs a motivated free user very little and still gives
 * paying users something real.
 *
 * Pure, and `now` is injected, like everything else in this package.
 */

import { FREE_DAILY_PROBLEM_LIMIT } from '@yuan-math/config';
import type { LearnerProfile } from './types.ts';

export type Tier = 'free' | 'premium' | 'family' | 'school';

/**
 * What the platform knows about a subscription.
 *
 * Mirrors Stripe's model closely on purpose: `status` and `currentPeriodEnd`
 * come straight from the webhook, so there is no second source of truth to
 * drift.
 */
export type Subscription = {
  tier: Tier;
  /** Stripe's subscription status, passed through unaltered. */
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'unpaid';
  /** Epoch milliseconds. Access continues to this moment even after cancelling. */
  currentPeriodEnd?: number;
  /** True when the subscriber has cancelled but the period has not ended. */
  cancelAtPeriodEnd?: boolean;
  /** Seats, for family and school plans. */
  seats?: number;
};

export type Entitlements = {
  tier: Tier;
  /** Undefined means unlimited. */
  dailyProblemLimit: number | undefined;
  advancedCourses: boolean;
  detailedProgress: boolean;
  adaptiveReview: boolean;
  /** Ships disabled regardless of tier until the AI tutor exists (§13). */
  aiTutor: boolean;
  parentDashboard: boolean;
  teacherTools: boolean;
  maxLearners: number;
};

const FREE: Entitlements = {
  tier: 'free',
  dailyProblemLimit: FREE_DAILY_PROBLEM_LIMIT,
  advancedCourses: false,
  detailedProgress: false,
  // Spaced review stays free: forgetting is not a premium problem, and a
  // platform that lets free users forget on purpose is not a teaching tool.
  adaptiveReview: true,
  aiTutor: false,
  parentDashboard: true,
  teacherTools: false,
  maxLearners: 1,
};

const PAID: Omit<Entitlements, 'tier' | 'maxLearners'> = {
  dailyProblemLimit: undefined,
  advancedCourses: true,
  detailedProgress: true,
  adaptiveReview: true,
  aiTutor: false,
  parentDashboard: true,
  teacherTools: false,
};

const BY_TIER: Record<Tier, Entitlements> = {
  free: FREE,
  premium: { ...PAID, tier: 'premium', maxLearners: 1 },
  family: { ...PAID, tier: 'family', maxLearners: 5 },
  school: { ...PAID, tier: 'school', teacherTools: true, maxLearners: 200 },
};

/**
 * Whether a subscription currently grants its tier.
 *
 * `past_due` deliberately still counts. A card that expired overnight is a
 * billing problem, not a reason to interrupt a child's lesson — Stripe will
 * retry, and the grace comes out of our pocket rather than their education.
 * `canceled` keeps access until the period they already paid for ends.
 */
export function isActive(subscription: Subscription | undefined, now: number): boolean {
  if (!subscription) return false;

  switch (subscription.status) {
    case 'active':
    case 'trialing':
    case 'past_due':
      return true;
    case 'canceled':
      // Paid until the end of the period; access ends with it.
      return subscription.currentPeriodEnd !== undefined && subscription.currentPeriodEnd > now;
    case 'incomplete':
    case 'unpaid':
      return false;
  }
}

export function entitlementsFor(subscription: Subscription | undefined, now: number): Entitlements {
  if (!isActive(subscription, now) || !subscription) return BY_TIER.free;
  return BY_TIER[subscription.tier] ?? BY_TIER.free;
}

/* -------------------------------------------------------------------------- */
/* The daily limit                                                            */
/* -------------------------------------------------------------------------- */

export type PracticeAllowance = {
  /** Graded attempts made today. Malformed input is not an attempt. */
  usedToday: number;
  /** Undefined when unlimited. */
  limit: number | undefined;
  remaining: number | undefined;
  exhausted: boolean;
};

/**
 * How much practice is left today.
 *
 * Counts against the learner's *local calendar* day, like the streak does — a
 * limit that resets at UTC midnight would cut a child off mid-evening in some
 * time zones and not others.
 */
export function practiceAllowance(
  profile: LearnerProfile,
  entitlements: Entitlements,
  now: number,
): PracticeAllowance {
  const limit = entitlements.dailyProblemLimit;
  const today = localDayKey(now);

  const usedToday = profile.attempts.filter(
    (attempt) => attempt.status !== 'malformed' && localDayKey(attempt.at) === today,
  ).length;

  if (limit === undefined) {
    return { usedToday, limit: undefined, remaining: undefined, exhausted: false };
  }

  const remaining = Math.max(0, limit - usedToday);
  return { usedToday, limit, remaining, exhausted: remaining === 0 };
}

function localDayKey(at: number): string {
  const date = new Date(at);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Whether a lesson may be opened.
 *
 * Always true. It exists as a function rather than a constant so the intent is
 * greppable and a future change has to be deliberate: reading a lesson is never
 * gated, on any tier, at any usage.
 */
export function canOpenLesson(): boolean {
  return true;
}

/** Whether more practice may be generated right now. */
export function canPractise(allowance: PracticeAllowance): boolean {
  return !allowance.exhausted;
}

/**
 * A sentence for a learner who has hit the cap.
 *
 * Written to be honest rather than pressuring: it says what they can still do,
 * because they can still do the thing that matters most.
 */
export function allowanceMessage(allowance: PracticeAllowance): string | undefined {
  if (allowance.limit === undefined || !allowance.exhausted) return undefined;
  return (
    `You have used today's ${allowance.limit} practice questions. Lessons, the Math Map and your ` +
    `progress stay open, and a new set unlocks tomorrow.`
  );
}
